import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { FC, SyntheticEvent, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import api from "../../../../api";
import { successToaster } from "../../../../helpers/toasters";

type AdvertiserPaymentMethodModalProps = {
  isOpen: boolean;
  advertiserId: string;
  advertiserName: string;
  hasExistingCard: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type FormValues = {
  cardFriendlyName: string;
  billingEmail: string;
  cardholderName: string;
};

const ModalHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "20px 24px",
  borderBottom: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const ModalTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const ModalSubtitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  marginTop: 4,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  padding: "24px !important",
  minWidth: 480,
}));

const FieldWrapper = styled("div")({
  marginBottom: 12,
});

const StyledTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: theme.palette.brand.white,
    borderRadius: 8,
    "& fieldset": {
      borderColor: theme.palette.brand.gray200,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.brand.blueAccent,
    },
  },
  "& .MuiInputBase-root": {
    fontSize: 13,
  },
}));

const CardElementWrapper = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: 12,
  borderRadius: 8,
  border: `1px solid ${theme.palette.brand.gray200}`,
  background: theme.palette.brand.white,
}));

const FormErrorBanner = styled("div")(({ theme }: { theme: Theme }) => ({
  marginTop: 8,
  padding: "10px 12px",
  borderRadius: 8,
  border: `1px solid ${theme.palette.brand.gray200}`,
  background: theme.palette.brand.white,
  color: theme.palette.brand.errorRed,
  fontSize: 13,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }: { theme: Theme }) => ({
  padding: "16px 24px",
  borderTop: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const CancelButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray700,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderColor: theme.palette.brand.gray300,
  borderRadius: 8,
  padding: "8px 14px",
}));

const SubmitButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderRadius: 8,
  padding: "8px 16px",
  minWidth: 110,
  boxShadow: "none",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    boxShadow: "none",
  },
}));

const AdvertiserPaymentMethodModal: FC<AdvertiserPaymentMethodModalProps> = ({
  isOpen,
  advertiserId,
  advertiserName,
  hasExistingCard,
  onClose,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    cardFriendlyName: "",
    billingEmail: "",
    cardholderName: "",
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormValues({ cardFriendlyName: "", billingEmail: "", cardholderName: "" });
    setFormErrorMessage("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement) as StripeCardElement | null;
    if (!cardElement) return;

    setIsLoading(true);

    try {
      const { payload: intentToken } = await api.stripeIntent();
      const { setupIntent, error: stripeFormError } = await stripe.confirmCardSetup(intentToken, {
        payment_method: {
          billing_details: { name: formValues.cardholderName },
          card: cardElement,
        },
      });

      if (setupIntent?.payment_method) {
        await api.advertiserStripeSave({
          bgAdvertiserId: advertiserId,
          billingEmail: formValues.billingEmail,
          friendlyName: formValues.cardFriendlyName,
          method: String(setupIntent.payment_method),
        });
        successToaster("Payment method was saved successfully.", 5000);
        onSuccess();
        handleClose();
      }

      if (stripeFormError?.message) {
        setFormErrorMessage(stripeFormError.message);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to save payment method. Please try again.";
      setFormErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <ModalHeader>
        <ModalTitle>{hasExistingCard ? "Edit payment method" : "Add payment method"}</ModalTitle>
        <ModalSubtitle>{advertiserName}</ModalSubtitle>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <StyledDialogContent>
          <FieldWrapper>
            <StyledTextField
              fullWidth
              variant="outlined"
              label="Friendly card name"
              name="cardFriendlyName"
              value={formValues.cardFriendlyName}
              onChange={handleInputChange}
              required
            />
          </FieldWrapper>
          <FieldWrapper>
            <StyledTextField
              fullWidth
              variant="outlined"
              label="Billing email"
              name="billingEmail"
              type="email"
              value={formValues.billingEmail}
              onChange={handleInputChange}
              required
            />
          </FieldWrapper>
          <FieldWrapper>
            <StyledTextField
              fullWidth
              variant="outlined"
              label="Cardholder name"
              name="cardholderName"
              value={formValues.cardholderName}
              onChange={handleInputChange}
              required
            />
          </FieldWrapper>
          <FieldWrapper>
            <CardElementWrapper>
              <CardElement />
            </CardElementWrapper>
            {formErrorMessage ? <FormErrorBanner>{formErrorMessage}</FormErrorBanner> : null}
          </FieldWrapper>
        </StyledDialogContent>

        <StyledDialogActions>
          <CancelButton variant="outlined" onClick={handleClose} disabled={isLoading}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit" disabled={!stripe || isLoading}>
            {isLoading ? "Saving…" : hasExistingCard ? "Save" : "Add"}
          </SubmitButton>
        </StyledDialogActions>
      </form>
    </Dialog>
  );
};

export default AdvertiserPaymentMethodModal;
